<template>
    <div class="stock-breakdown-wrapper" @mouseleave="showTooltip = false">
        <div
            :class="['stock-breakdown-view', { underline: warehouseEntries && warehouseEntries.length }]"
            tabindex="0"
            @click="showBreakdownModal = true"
            @mouseenter="openTooltip"
            @focus="openTooltip"
            @blur="showTooltip = false"
        >
            {{ displayStock }} unit{{
                totalStock == 0 || totalStock > 1 ? "s" : ""
            }}
        </div>
        <div v-if="showTooltip" class="stock-breakdown-tooltip" :style="tooltipStyle" role="tooltip">
            <div v-if="!warehouseEntries || !warehouseEntries.length" class="tooltip-empty">
                No stock breakdown available
            </div>
            <div v-else>
                <div class="tooltip-location" v-for="warehouseEntry of warehouseEntries" :key="warehouseEntry.id">
                    <div class="tooltip-location-title">
                        <strong>{{ warehouseEntry.warehouse && warehouseEntry.warehouse.name ? warehouseEntry.warehouse.name : "Unknown location" }}</strong>
                        <span>{{ warehouseEntry.stock || 0 }} units</span>
                    </div>
                    <div class="tooltip-lot-list">
                        <div
                            class="tooltip-lot"
                            v-for="batch of visibleBatches(warehouseEntry)"
                            :key="batch.batch_no"
                        >
                            <span class="tooltip-lot-number">LOT {{ batch.batch_no }}</span>
                            <span>{{ batch.qty || 0 }} total</span>
                            <span>{{ batch.frontRoomQty || 0 }} front</span>
                            <span>{{ batch.backRoomQty || 0 }} back</span>
                            <span v-for="room of batch.customRooms || []" :key="room.name">{{ room.name }}: {{ room.qty }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div>
            <CustomModal
                v-if="warehouseEntries && warehouseEntries.length"
                :title="`${totalStock} unit${totalStock == 0 || totalStock > 1 ? 's' : ''} across ${warehouseEntries.length} location${
                    warehouseEntries.length > 1 || warehouseEntries.length == 0 ? 's' : ''
                }`"
                :show="showBreakdownModal"
                @close="showBreakdownModal = false"
            >
                <div class="my-3" style="max-height: 300px; overflow-y: scroll">
                    <div>
                        <div class="mb-2" v-for="warehouseEntry of warehouseEntries" :key="warehouseEntry.id">
                            <div>
                                <p>
                                    Total Stock in
                                    <strong>
                                        {{ warehouseEntry.warehouse.name }} -
                                        {{ warehouseEntry.stock }}
                                    </strong>
                                </p>
                                <div class="my-3">
                                    <StockBreakdownRoomQtyViewer
                                        :warehouseEntry="warehouseEntry"
                                        title="Front Room"
                                        roomQtyKey="frontRoomQty"
                                        roomStockKey="frontRoomStock"
                                    />
                                    <StockBreakdownRoomQtyViewer
                                        :warehouseEntry="warehouseEntry"
                                        title="Back Room"
                                        roomQtyKey="backRoomQty"
                                        roomStockKey="backRoomStock"
                                    />
                                    <StockBreakdownRoomQtyViewer
                                        v-for="customRoom of warehouseEntry.customRooms || []"
                                        :key="customRoom.name"
                                        :warehouseEntry="customRoom"
                                        :title="customRoom.name"
                                        roomQtyKey="qty"
                                        roomStockKey="stock"
                                    />
                                </div>
                            </div>
                            <hr class="my-3" />
                        </div>
                    </div>
                </div>
            </CustomModal>
        </div>
    </div>
</template>

<script>
import CustomModal from "../layout/CustomModal.vue"
import StockBreakdownRoomQtyViewer from "./StockBreakdownRoomQtyViewer.vue"

export default {
    data() {
        return {
            showBreakdownModal: false,
            showTooltip: false,
            tooltipStyle: {},
        }
    },
    components: { CustomModal, StockBreakdownRoomQtyViewer },
    props: {
        showAllStock: {
            type: Boolean,
            default: false,
        },
        totalBackRoomStock: {
            type: Number,
            default: 0,
        },
        totalCustomRoomStock: {
            type: Number,
            default: 0,
        },
        totalFrontRoomStock: {
            type: Number,
            default: 0,
        },
        showBackRoomStock: {
            type: Boolean,
            default: false,
        },
        totalStock: {
            type: Number,
            default: 0,
        },
        warehouseEntries: {
            type: Array,
            default: () => [],
        },
        selectedRoomKey: {
            type: String,
            default: "",
        },
        selectedRoomName: {
            type: String,
            default: "",
        },
    },
    computed: {
        displayStock() {
            if (this.showAllStock) return this.totalStock
            if (this.selectedRoomKey && !["FRONT_ROOM", "BACK_ROOM"].includes(this.selectedRoomKey)) {
                return this.totalCustomRoomStock
            }
            return this.showBackRoomStock ? this.totalBackRoomStock : this.totalFrontRoomStock
        },
    },
    methods: {
        openTooltip(event) {
            this.showTooltip = true
            const rect = event.currentTarget.getBoundingClientRect()
            const tooltipWidth = 360
            const horizontalPadding = 12
            const left = Math.min(
                Math.max(horizontalPadding, rect.left),
                window.innerWidth - tooltipWidth - horizontalPadding
            )
            const top = rect.bottom + 8
            this.tooltipStyle = {
                left: `${left}px`,
                top: `${top}px`,
            }
        },
        visibleBatches(warehouseEntry) {
            return (warehouseEntry.batches || []).filter((batch) => Number(batch.qty || 0) > 0)
        },
    },
    watch: {},
}
</script>

<style lang="scss" scoped>
.stock-breakdown-wrapper {
    position: relative;
    display: inline-block;
}

.stock-breakdown-view {
    cursor: pointer;
    outline: none;
    &.underline {
        text-decoration: underline;
    }
}

.stock-breakdown-tooltip {
    position: fixed;
    z-index: 9999;
    width: 360px;
    max-width: calc(100vw - 24px);
    max-height: 280px;
    overflow-y: auto;
    pointer-events: none;
    padding: 12px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 6px;
    background: #ffffff;
    box-shadow: 0 10px 28px rgba(0, 0, 0, 0.16);
    color: #222222;
    font-size: 12px;
    line-height: 1.4;
    text-decoration: none;
    white-space: normal;
}

.tooltip-location + .tooltip-location {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.tooltip-location-title {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 6px;
}

.tooltip-lot-list {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.tooltip-lot {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 10px;
}

.tooltip-lot-number {
    font-weight: 600;
}

.tooltip-empty {
    color: rgba(0, 0, 0, 0.65);
}
</style>
